"use client"

import { useState, useEffect } from "react"
import { useInView } from "react-intersection-observer"
import { Leaf, Coffee, Droplets, TreePine, ChevronLeft, ChevronRight } from "lucide-react"
import "../assets/css/proposed-projects.css"

import nurseryImg from "../assets/images/proposed/nursery.webp"
import jamImg from "../assets/images/proposed/jam.webp"
import vanilaImg from "../assets/images/proposed/vanilla.webp"
import teaImg from "../assets/images/proposed/tea.webp"

export default function ProposedProjects() {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    const checkScreen = () => setIsDesktop(window.innerWidth >= 768)
    checkScreen()
    window.addEventListener("resize", checkScreen)
    return () => window.removeEventListener("resize", checkScreen)
  }, [])

  const projects = [
    {
      icon: <Coffee className="w-6 h-6" />,
      title: "Vanilla Cultivation",
      description:
        "Planning to create a range of products using premium vanilla, focusing on sustainable farming and value-added processing for international markets.",
      image: vanilaImg,
      features: [
        "Premium quality vanilla beans",
        "Sustainable cultivation methods",
        "Export market focus",
        "Farmer training programs",
      ],
      timeline: "2024-2025",
      progress: 25,
      color: "#8b5cf6", // purple
    },
    {
      icon: <Leaf className="w-6 h-6" />,
      title: "Tea",
      description:
      "Planning to create a variety of products using premium tea, with a focus on quality, sustainability, and value-added processing for local and international markets.",
      image: teaImg,
      features: [
        "Organic certification",
        "Traditional Ceylon tea",
        "Eco-friendly processing",
        "Direct trade partnerships",
      ],
      timeline: "2024-2026",
      progress: 15,
      color: "#16a34a", // green
    },
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Chili Processing Factory",
      description:
        "We plan to establish a factory focused on producing chili-based products such as sauces and jams, utilizing advanced processing methods and ensuring high quality for both local and export markets.",
      image: jamImg,
      features: [
        "Quality control systems",
        "Multiple product lines",
        "Export capabilities",
      ],
      timeline: "2025-2026",
      progress: 35,
      color: "#dc2626", // red
    },
    {
      icon: <TreePine className="w-6 h-6" />,
      title: "Commercial Nursery",
      description:
        "Large-scale nursery operation to supply quality plants, seedlings, and gardening supplies to the market.",
      image: nurseryImg,
      features: ["Wide variety of plants", "Disease-free seedlings", "Retail and wholesale", "Gardening consultancy"],
      timeline: "2024-2025",
      status: "Development",
      progress: 45,
      color: "#eab308", // yellow
    },
  ]

  // Cards per slide: 2 for desktop, 1 for mobile
  const cardsPerSlide = isDesktop ? 2 : 1
  const maxSlides = Math.ceil(projects.length / cardsPerSlide)

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoPlaying || !inView) return

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % maxSlides)
    }, 4000) // Change slide every 4 seconds

    return () => clearInterval(interval)
  }, [isAutoPlaying, maxSlides, inView])

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 8000)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % maxSlides)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 8000)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + maxSlides) % maxSlides)
    setIsAutoPlaying(false)
    setTimeout(() => setIsAutoPlaying(true), 8000)
  }

  return (
    <section id="projects" ref={ref} className="agri-projects-wrapper">
      <div className="agri-projects-container">
        <div className="agri-header-section">
          <h2 className={`agri-main-title ${inView ? "agri-title-visible" : ""}`}>Proposed Projects</h2>
          <div className={`agri-title-divider ${inView ? "agri-divider-visible" : ""}`}></div>
          <p className={`agri-header-subtitle ${inView ? "agri-subtitle-visible" : ""}`}>
            Expanding our horizons with innovative agricultural ventures and value-added processing facilities
          </p>
        </div>

        <div className="agri-carousel-wrapper">
          {/* Navigation Arrows */}
          <button
            className="agri-nav-btn agri-nav-left"
            onClick={prevSlide}
            aria-label="Previous projects"
            disabled={maxSlides <= 1}
          >
            <ChevronLeft className="agri-arrow-icon" />
          </button>

          <button
            className="agri-nav-btn agri-nav-right"
            onClick={nextSlide}
            aria-label="Next projects"
            disabled={maxSlides <= 1}
          >
            <ChevronRight className="agri-arrow-icon" />
          </button>

          {/* Projects Grid */}
          <div className={`agri-projects-viewport ${inView ? "agri-viewport-visible" : ""}`}>
            <div
              className="agri-slides-container"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
              }}
            >
              {Array.from({ length: maxSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="agri-slide-group">
                  {projects
                    .slice(slideIndex * cardsPerSlide, slideIndex * cardsPerSlide + cardsPerSlide)
                    .map((project, projectIndex) => (
                      <div key={projectIndex} className="agri-project-card">
                        <div className="agri-card-image">
                          <img
                            src={project.image || "/placeholder.svg"}
                            alt={project.title}
                            loading="lazy"
                            className="agri-project-img"
                          />
                        </div>

                        <div className="agri-card-content">
                          <div className="agri-card-header">
                            <div className="agri-project-icon" style={{ backgroundColor: `${project.color}22` }}>
                              <div style={{ color: project.color }}>{project.icon}</div>
                            </div>
                            <div className="agri-project-meta">
                              <h3 className="agri-project-title" style={{ color: project.color }}>
                                {project.title}
                              </h3>
                            </div>
                          </div>

                          <p className="agri-project-desc">{project.description}</p>

                          <div className="agri-features-section">
                            <h4 className="agri-features-title">Key Features:</h4>
                            <ul className="agri-features-list">
                              {project.features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="agri-feature-item">
                                  <span className="agri-feature-bullet" style={{ backgroundColor: project.color }}></span>
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="agri-indicators">
            {Array.from({ length: maxSlides }).map((_, index) => (
              <button
                key={index}
                className={`agri-indicator ${index === currentSlide ? "agri-indicator-active" : ""}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                style={{
                  backgroundColor: index === currentSlide ? "#dc2626" : "rgba(220, 38, 38, 0.3)",
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Background decoration */}
      <div className="agri-bg-element agri-bg-element-1"></div>
      <div className="agri-bg-element agri-bg-element-2"></div>
    </section>
  )
}